import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, User, ArrowRight, AlertCircle } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Admin · Fira Smart Desk";
    // If already logged in, go to dashboard
    const t = localStorage.getItem("fira_admin_token");
    if (t) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/admin/login`, {
        username: username.trim(),
        password,
      });
      localStorage.setItem("fira_admin_token", data.token);
      localStorage.setItem("fira_admin_user", data.username);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080a] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div
        className="glow-orb"
        style={{
          width: 480,
          height: 480,
          top: "-10%",
          left: "-10%",
          background: "#ff6699",
          opacity: 0.25,
        }}
      />
      <div
        className="glow-orb"
        style={{
          width: 380,
          height: 380,
          bottom: "-15%",
          right: "-10%",
          background: "#ff6699",
          opacity: 0.18,
        }}
      />

      <form
        onSubmit={onSubmit}
        className="relative z-10 w-full max-w-md p-8 sm:p-10 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-[0_30px_90px_-30px_rgba(255,102,153,0.4)]"
        data-testid="admin-login-form"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#ff6699]/10 border border-[#ff6699]/30 mb-5">
            <Lock size={22} className="text-[#ff6699]" />
          </div>
          <h1 className="font-display font-bold text-2xl tracking-tight">
            Admin Login
          </h1>
          <p className="mt-2 text-sm text-white/55">
            Fira Smart Desk Analytics Dashboard
          </p>
        </div>

        {error && (
          <div
            className="mb-5 flex items-start gap-2 px-4 py-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm"
            data-testid="admin-login-error"
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/55 mb-2">
              Username
            </label>
            <div className="relative">
              <User
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#ff6699] focus:bg-white/[0.06] outline-none text-white placeholder-white/30 transition-colors"
                placeholder="FiraAdmin"
                data-testid="admin-username-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/55 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#ff6699] focus:bg-white/[0.06] outline-none text-white placeholder-white/30 transition-colors"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-cta w-full mt-7"
          data-testid="admin-login-submit"
        >
          <span>{loading ? "Signing in..." : "Sign In"}</span>
          {!loading && <ArrowRight size={18} strokeWidth={2.4} />}
        </button>
      </form>
    </div>
  );
}
